import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  adminListSeasons,
  fetchAllSeasonRankings,
  type RankingKind,
  type SeasonMeta,
} from '@/client/admin.client';
import { downloadCsv, type CsvColumn } from '@/lib/download';

const LEAGUE_COLUMNS: CsvColumn[] = [
  { key: 'rank', header: 'Rank' },
  { key: 'account', header: 'Account' },
  { key: 'nickname', header: 'Nickname' },
  { key: 'characterId', header: 'CharacterId' },
  { key: 'score', header: 'Score' },
  { key: 'playCount', header: 'PlayCount' },
  { key: 'timestamp', header: 'Timestamp' },
  { key: 'seasonId', header: 'SeasonId' },
];

const DEGEN_COLUMNS: CsvColumn[] = [
  { key: 'rank', header: 'Rank' },
  { key: 'account', header: 'Account' },
  { key: 'nickname', header: 'Nickname' },
  { key: 'characterId', header: 'CharacterId' },
  { key: 'cumulativeScore', header: 'CumulativeScore' },
  { key: 'runCount', header: 'RunCount' },
  { key: 'bustCount', header: 'BustCount' },
  { key: 'lastRunAt', header: 'LastRunAt' },
  { key: 'seasonId', header: 'SeasonId' },
];

export function SeasonRankingsPage() {
  const [kind, setKind] = useState<RankingKind>('LEAGUE');
  const [seasons, setSeasons] = useState<SeasonMeta[]>([]);
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const columns = kind === 'DEGEN' ? DEGEN_COLUMNS : LEAGUE_COLUMNS;

  useEffect(() => {
    let cancelled = false;
    setSeasons([]);
    setSeasonId(null);
    setRows([]);
    setError(null);
    adminListSeasons(kind)
      .then((s) => {
        if (!cancelled) {
          setSeasons(s);
          if (s[0]) setSeasonId(s[0].id);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(String((e as Error).message ?? e));
      });
    return () => {
      cancelled = true;
    };
  }, [kind]);

  useEffect(() => {
    if (seasonId == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]); // clear stale rows on new fetch
    fetchAllSeasonRankings(kind, seasonId)
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((e) => {
        if (!cancelled) setError(String((e as Error).message ?? e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind, seasonId]);

  function handleExport() {
    if (seasonId == null) return;
    downloadCsv(`${kind.toLowerCase()}-season${seasonId}-rankings.csv`, rows, columns);
  }

  return (
    <div className="space-y-4 p-4">
      <h1 className="text-xl font-semibold">Season Rankings</h1>
      <div className="flex items-center gap-3">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as RankingKind)}
          className="border rounded px-2 py-1"
        >
          <option value="LEAGUE">League</option>
          <option value="DEGEN">Degen</option>
        </select>
        <select
          value={seasonId ?? ''}
          onChange={(e) => setSeasonId(e.target.value === '' ? null : Number(e.target.value))}
          className="border rounded px-2 py-1"
        >
          {seasons.length === 0 && <option value="">(no seasons)</option>}
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>{`#${s.id} ${s.name}`}</option>
          ))}
        </select>
        <Button size="sm" disabled={loading || rows.length === 0} onClick={handleExport}>
          Export CSV
        </Button>
        {loading && <span className="text-sm text-muted-foreground">loading…</span>}
      </div>
      {error && <div className="text-sm text-red-500">{error}</div>}
      <div className="text-sm text-muted-foreground">{rows.length} rows</div>
      <div className="max-h-[70vh] overflow-auto border rounded">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.key}>{c.header}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, 500).map((row, i) => (
              <TableRow key={String(row.rank ?? i)}>
                {columns.map((c) => (
                  <TableCell key={c.key} className="font-mono text-xs break-all">
                    {row[c.key] === undefined || row[c.key] === null ? '-' : String(row[c.key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default SeasonRankingsPage;
